import Close16 from '@carbon/icons-react/es/close/16';
import Save16 from '@carbon/icons-react/es/save/16';
import { Col, Row, Space, Tooltip } from 'antd';
import moment from 'moment';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { BillRateSourceType, FinancialsProjectSettings } from 'api/project';
import { Button } from 'components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { PageLoading } from 'components/atoms/loading';
import { Radio } from 'components/atoms/radio';
import { Select } from 'components/atoms/select';
import { DescriptiveOption } from 'components/atoms/select/custom-components';
import { ProjectFinancialsSettingsContext } from 'contexts/project-financials-settings';
import { useTranslate } from 'hooks/use-translation';
import { useRateCardsQuery } from 'queries/rate-cards';
import { TeamMembersRateInfo } from '../member-rate-info-table';
import { CustomBillRateLookup } from '../types';
import { BackDateCustomRatesModal } from './set-backdate-modal';
import { EffectiveDateWrapper, EffectiveFrom, FooterWrapper } from './styles';

const StyledRow = styled(Row)`
  margin-bottom: var(--spacing-06);
`;

const SelectWrapper = styled.div`
  height: 40px;
`;

export const EditBillRateDrawer: FC<{
  visible: boolean;
  projectId: number;
  saveInProgress: boolean;
  pageInfo: FinancialsProjectSettings | undefined;
  onClose: () => void;
  onSave: (data: FinancialsProjectSettings) => void;
}> = ({ pageInfo, projectId, visible, onClose, onSave, saveInProgress }) => {
  const { permissionsMap } = useContext(ProjectFinancialsSettingsContext);

  const {
    projectCustomBillRate: { canWrite: canUpdateCustomBillRates },
  } = permissionsMap;
  const translate = useTranslate();

  const [isInitialised, setInitialised] = useState(false);
  // override the already saved config
  const [overrideConfig, setOverrideConfig] = useState<{
    rateCard?: {
      label: string;
      value: number;
    };
    overrideBillRate: boolean;
  }>({ overrideBillRate: false });

  // custom billrates to override the default one
  const [customBillRateLookup, setCustomBillRateLookup] =
    useState<CustomBillRateLookup>({});
  const [isModalVisible, setModalVisiblity] = useState(false);

  useEffect(() => {
    // initialising data and the custom rates from the api
    if (pageInfo && !isInitialised) {
      const lookup = pageInfo.projectCustomRates.reduce(
        (map, entry) => ({
          ...map,
          [`${entry.sourceId}${
            entry.replacedUserId ? `_${entry.replacedUserId}_` : '_'
          }${entry.sourceType}`]: {
            sourceId: entry.sourceId,
            rate: entry.rate,
            sourceType: entry.sourceType,
            currency: entry.currency,
            ...(entry.replacedUserId &&
            entry.sourceType === BillRateSourceType.TEAM
              ? { replacedUserId: entry.replacedUserId }
              : {}),
          },
        }),
        {},
      );

      setCustomBillRateLookup(lookup);
      setOverrideConfig({
        ...(pageInfo.rateCard
          ? {
              rateCard: {
                label: pageInfo.rateCard.rateCardName,
                value: pageInfo.rateCard.rateCardId,
              },
            }
          : {}),
        overrideBillRate:
          !pageInfo.rateCard && !!pageInfo.projectCustomRates.length,
      });
      setInitialised(true);
    }
  }, [isInitialised, pageInfo]);

  const { data: rateCardsList } = useRateCardsQuery(true);
  const rateCardsOptions = useMemo(
    () =>
      (rateCardsList ?? [])
        .filter((rateCard) => rateCard.enabled)
        .map((rateCard) => ({
          label: rateCard.rateCardName,
          value: rateCard.rateCardId,
          description: (
            <EffectiveDateWrapper>
              <EffectiveFrom>
                {translate('projects.create.rate_cards.effective_from')}
              </EffectiveFrom>
              {moment(rateCard.activeVersion?.effectiveDate).format(
                'DD MMM YY',
              )}
            </EffectiveDateWrapper>
          ),
        })),
    [rateCardsList, translate],
  );

  return (
    <CustomLayoutDrawer
      width={800}
      visible={visible}
      closeIcon={<Close16 />}
      onClose={onClose}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          {translate('project.settings.budget.rate.edit.bill.rate')}
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          {pageInfo ? (
            <div>
              <StyledRow align="middle">
                <Col span={6}>
                  <Radio
                    checked={!overrideConfig.overrideBillRate}
                    onClick={() =>
                      setOverrideConfig((state) => ({
                        ...state,
                        overrideBillRate: false,
                      }))
                    }
                  >
                    {translate(
                      'project.settings.budget.rate.edit.bill.rate.choose.rate.card',
                    )}
                  </Radio>
                </Col>
                <Col span={12}>
                  <SelectWrapper>
                    {!overrideConfig.overrideBillRate ? (
                      <Row align="middle">
                        <Col span={20}>
                          <Select
                            value={overrideConfig.rateCard}
                            onChange={(rateCard) => {
                              setOverrideConfig((config) => ({
                                ...config,
                                rateCard,
                              }));
                            }}
                            components={{ Option: DescriptiveOption }}
                            options={rateCardsOptions}
                            isClearable={false}
                            menuPlacement="auto"
                            placeholder={translate(
                              'project.settings.budget.rate.edit.bill.rate.select.rate.card',
                            )}
                          />
                        </Col>
                      </Row>
                    ) : null}
                  </SelectWrapper>
                </Col>
                <Col span={24}>
                  <Tooltip
                    title={
                      canUpdateCustomBillRates
                        ? null
                        : translate(
                            'project.settings.budget.rate.edit.bill.rate.manual.disabled.tooltip',
                          )
                    }
                  >
                    <Radio
                      checked={overrideConfig.overrideBillRate}
                      onClick={() =>
                        setOverrideConfig((state) => ({
                          ...state,
                          overrideBillRate: true,
                        }))
                      }
                      style={{ marginTop: 8 }}
                      disabled={!canUpdateCustomBillRates}
                    >
                      <Space direction="vertical">
                        <span>
                          {translate(
                            'project.settings.budget.rate.edit.bill.rate.manual',
                          )}
                        </span>
                      </Space>
                    </Radio>
                  </Tooltip>
                </Col>
              </StyledRow>
              <BackDateCustomRatesModal
                visible={isModalVisible}
                onOk={(enableBackDate) => {
                  const { rateCardVersionId, rateCard, ...config } = pageInfo;

                  const projectCustomRates = Object.keys(
                    customBillRateLookup,
                  ).map((id) => {
                    const { sourceType, rate, replacedUserId, currency } =
                      customBillRateLookup[id];
                    const sourceId = id.split('_')[0];

                    return {
                      sourceType,
                      rate,
                      currency,
                      ...(replacedUserId ? { replacedUserId } : {}),
                      projectFinancialsId: pageInfo.projectFinancialsId,
                      sourceId: Number(sourceId),
                    };
                  });

                  onSave({
                    ...config,
                    ...(!overrideConfig.overrideBillRate
                      ? { rateCardId: overrideConfig.rateCard?.value }
                      : {}),
                    projectCustomRates: overrideConfig.overrideBillRate
                      ? projectCustomRates
                      : [],
                    billRatesBackdated: enableBackDate,
                  });
                  setModalVisiblity(false);
                }}
                onCancel={() => setModalVisiblity(false)}
              />

              <TeamMembersRateInfo
                overrideRateCardId={overrideConfig.rateCard?.value}
                readonly={
                  !overrideConfig.overrideBillRate || !canUpdateCustomBillRates
                }
                overrideBillRate={overrideConfig.overrideBillRate}
                customBillRateMap={customBillRateLookup}
                updateBillRateLookUp={setCustomBillRateLookup}
                projectId={projectId}
                data={pageInfo}
              />
            </div>
          ) : (
            <PageLoading />
          )}
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <FooterWrapper>
            <Button onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              icon={<Save16 />}
              iconPlacement="right"
              type="primary"
              loading={saveInProgress}
              onClick={() => {
                setModalVisiblity(true);
              }}
            >
              <FormattedMessage id="save" />
            </Button>
          </FooterWrapper>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
